import { z } from 'zod';
import { TaskGroupView } from './task-group.js';
import { CalendarDate, DependencyType, TaskKind, TaskView } from './project.js';
/**
 * Gantt + critical path — APM-503.
 *
 * ⚠️ Mọi view và chart nhận `asOf?: Date` (luật #7). Ở Gantt, `asOf` quyết định
 * vạch "hôm nay", cái gì bị coi là trễ, và mốc so sánh với baseline — nên nó là
 * thứ dùng được ngay bây giờ, không phải một prop để dành cho Sprint 9.
 */
/**
 * Chuyển sang `project.ts` ở APM-ROLE-r và **re-export** ở đây.
 *
 * `TaskView` cần nó, mà `gantt.ts` đã import `project.ts` — khai ở đây thì thành
 * vòng import. Giữ lại tên xuất khẩu để mọi chỗ đang `import { TaskKind } from
 * './gantt.js'` không phải đổi.
 */
export { TaskKind } from './project.js';
export const GanttBar = z.object({
    task: TaskView,
    kind: TaskKind,
    /** Ngày bắt đầu/kết thúc theo kế hoạch hiện tại. `null` khi task chưa có ngày. */
    startDate: CalendarDate.nullable(),
    dueDate: CalendarDate.nullable(),
    /** Kế hoạch gốc, để vẽ thanh mờ bên dưới. `null` khi chưa chốt baseline. */
    baselineStartDate: CalendarDate.nullable(),
    baselineDueDate: CalendarDate.nullable(),
    /** Nằm trên đường găng: trễ một ngày là cả dự án trễ một ngày. */
    critical: z.boolean(),
    /** Trễ được bao nhiêu ngày mà không đẩy ngày kết thúc dự án. */
    totalFloat: z.number().int(),
    /**
     * Kế hoạch đang vi phạm phụ thuộc bao nhiêu ngày. `0` là hợp lệ.
     * Đây là con số hiện lên khi người dùng kéo một thanh về quá sớm.
     */
    violationDays: z.number().int().nonnegative(),
    /** Ngày sớm nhất task này bắt đầu được, xét mọi phụ thuộc. */
    earliestStartDate: CalendarDate.nullable(),
    /** Chậm so với baseline bao nhiêu ngày. Âm là sớm hơn kế hoạch. */
    slippageDays: z.number().int().nullable(),
});
export const GanttLink = z.object({
    id: z.uuid(),
    fromTaskId: z.uuid(),
    toTaskId: z.uuid(),
    type: DependencyType,
    lagDays: z.number().int(),
    /** Cả hai đầu đều nằm trên đường găng ⇒ mũi tên vẽ đậm. */
    critical: z.boolean(),
});
export const GanttWindowQuery = z.object({
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(500).default(200),
});
export const GanttView = z.object({
    page: z
        .object({
        offset: z.number().int(),
        limit: z.number().int(),
        total: z.number().int(),
        offPageLinks: z.number().int(),
    })
        .optional(),
    projectId: z.uuid(),
    /** Khung thời gian nhỏ nhất bao trọn mọi thanh. `null` khi chưa task nào có ngày. */
    windowStart: CalendarDate.nullable(),
    windowEnd: CalendarDate.nullable(),
    baselineSetAt: z.iso.datetime().nullable(),
    bars: z.array(GanttBar),
    links: z.array(GanttLink),
    /**
     * Nhóm việc của dự án — APM-1306.
     *
     * Đi kèm Gantt thay vì để client gọi thêm một endpoint: danh sách task đã
     * được **xếp theo nhóm** ở server, nên hai thứ phải tới cùng nhau, nếu không
     * client sẽ vẽ tiêu đề nhóm theo một danh sách và xếp hàng theo danh sách kia.
     */
    groups: z.array(TaskGroupView),
    /** Tổng số ngày đường găng — độ dài tối thiểu của dự án. */
    criticalPathDays: z.number().int().nonnegative(),
});
/**
 * Kéo một thanh Gantt: đổi ngày, không đổi gì khác.
 *
 * Endpoint riêng thay vì dùng `PATCH /tasks/:id`: nó trả kèm `warnings[]` khi
 * lịch mới vi phạm phụ thuộc, và trả lại toàn bộ Gantt đã tính lại — kéo một
 * thanh có thể đổi đường găng của cả dự án, nên client không đoán được.
 */
export const RescheduleTaskInput = z.object({
    startDate: CalendarDate,
    dueDate: CalendarDate,
});
export const SetTaskKindInput = z.object({
    kind: TaskKind,
});
//# sourceMappingURL=gantt.js.map